/**
 * Resolve an employee's non-billable default job and phase for the locked ITE
 * panel, shared by web and NativeScript.
 *
 * Pure TypeScript. The parameter types are MINIMAL STRUCTURAL shapes rather
 * than `ForemanJobDetail` / `ForemanPhase`, because `libs/xplat/utils` is
 * tagged `type:util` + `framework:agnostic` and `@ups/xplat/api/dto` is
 * `type:lib` + `framework:angular`, an edge `@nx/enforce-module-boundaries`
 * rejects on both axes (`.eslintrc.json` depConstraints). Callers pass the real
 * DTOs unchanged, since TypeScript matches these structurally, and a DTO field
 * rename still fails loudly at the call site.
 */

import { ForemanPhaseLike } from './foreman-shapes';

/** The job fields this module reads. `ForemanJobDetail` satisfies it. */
export interface NonBillableJobLike {
  vpJobID?: string | null;
  isNonBillableDefault?: boolean | null;
  nonBillableDefaultPhase?: string | null;
  /**
   * Read ONLY to answer "is the non-billable phase reachable on this job at all".
   * See the narrow-never-to-zero invariant in {@link isLeaveAllowedOnWorkOrder}.
   */
  workOrders?: ForemanPhaseLike[] | null;
}

function isFlaggedNonBillableDefault(job: NonBillableJobLike): boolean {
  if (job.isNonBillableDefault === true) return true;
  // PascalCase if a serializer skips camelCase transform.
  return (
    (job as unknown as { IsNonBillableDefault?: boolean })
      .IsNonBillableDefault === true
  );
}

/**
 * True when this single job is the employee's non-billable default, the one job
 * VAC / SICK / HOL may be booked against.
 *
 * This is the list-free form of the flag check {@link findNonBillableDefaultJob}
 * already does, exported because the NativeScript time-entry component holds one
 * selected job rather than a list and would otherwise re-implement the
 * camelCase/PascalCase pair. Two copies of that pair is exactly how holiday got
 * left out of the web lock.
 *
 * 🔴 FAILS CLOSED on a missing job. `foremanJobService.selectedJob()` is
 * undefined before a job is chosen and mid route transition, and the honest
 * reading there is "no job has been PROVEN non-billable", not "any job will do".
 * Failing open would flash the leave fields open on whatever loads next.
 *
 * The flag comparison stays strict `=== true` rather than truthy: the DTO field
 * is nullable, and a stray non-boolean must not widen a gate whose false
 * positive books vacation against customer-billable work.
 */
export function isNonBillableDefaultJob(
  job: NonBillableJobLike | null | undefined
): boolean {
  return job ? isFlaggedNonBillableDefault(job) : false;
}

function phaseFromJob(
  job: NonBillableJobLike | null | undefined
): string | null {
  if (!job) return null;
  const camel = job.nonBillableDefaultPhase?.trim();
  if (camel) return camel;
  const pascal = (
    job as unknown as { NonBillableDefaultPhase?: string | null }
  ).NonBillableDefaultPhase?.trim();
  return pascal || null;
}

/**
 * Primary source: Sparta job list flag `isNonBillableDefault` (set when the API
 * pins bPREH.udNBJobNum). That path does not need vp-api `nbdefaultjob`, which
 * 404s in prod when Viewpoint API has not shipped the route.
 *
 * Fallback: match `nbJobNum` (from a live vp-api call) by trimmed/uppercased
 * vpJobID, only used when the list flag is absent (older API) and the client
 * still has a number.
 */
export function findNonBillableDefaultJob<T extends NonBillableJobLike>(
  jobs: T[],
  nbJobNum: string | null | undefined
): T | null {
  const flagged = jobs.find((j) => isFlaggedNonBillableDefault(j));
  if (flagged) return flagged;

  const target = nbJobNum?.trim().toUpperCase();
  if (!target) return null;
  return (
    jobs.find((j) => (j.vpJobID ?? '').trim().toUpperCase() === target) ?? null
  );
}

/** Phase code from the flagged job DTO, or an optional client-side fallback. */
export function resolveNonBillableDefaultPhase(
  nbJob: NonBillableJobLike | null,
  fallbackPhase?: string | null
): string | null {
  const fromJob = phaseFromJob(nbJob);
  if (fromJob) return fromJob;
  const fromFallback = fallbackPhase?.trim();
  return fromFallback || null;
}

/**
 * Normalize VP phase codes for comparison (trim, upper, strip whitespace, strip trailing dots).
 *
 * 🔴 WHITESPACE IS REMOVED BEFORE TRAILING DOTS AND THE ORDER IS LOAD-BEARING. VP padding
 * interleaves spaces and dots ("SFTDEV.    ."), so stripping dots first removes the final
 * dot, hits a space, and stops, yielding "SFTDEV." while the unpadded "SFTDEV." yields
 * "SFTDEV". Two spellings of one phase would never compare equal.
 *
 * Kept deliberately identical to the server's `NonBillableEarnCodeRule.NormalizePhaseToken`.
 * Change one without the other and the UI surfaces a phase the submit guard rejects.
 */
export function normalizeNbPhaseToken(raw: string | null | undefined): string {
  return (raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/\.+$/g, '');
}

/**
 * True when a single work order IS the employee's Viewpoint udNBPhase.
 *
 * Extracted from {@link findNonBillableDefaultPhase} so the leave GATE
 * ({@link isLeaveAllowedOnWorkOrder}) and the work-order VISIBILITY helper
 * ({@link ensureNonBillablePhaseVisible}) cannot drift apart on the question
 * "which work order is the non-billable phase". Two matchers answering that
 * differently would show the employee a phase they then cannot book against.
 *
 * A blank `nbPhase` never matches: the caller decides what an absent udNBPhase
 * means, and for the leave gate that is "phase unenforced", not "every phase".
 */
export function matchesNonBillablePhase(
  workOrder: ForemanPhaseLike | null | undefined,
  nbPhase: string | null | undefined
): boolean {
  const target = normalizeNbPhaseToken(nbPhase);
  if (!target || !workOrder) return false;

  const code = normalizeNbPhaseToken(workOrder.phaseName);
  const desc = normalizeNbPhaseToken(workOrder.description);
  // Exact first, then prefix in both directions.
  //
  // Exactly ONE of the three length guards is load-bearing, and it is the one
  // on `target.startsWith(code)`: a null/empty phaseName normalizes to '' and
  // `target.startsWith('')` is ALWAYS TRUE, so without it find() locks onto the
  // empty-named work order — which MergeForemanPhases sorts FIRST (PR 2032 review).
  //
  // The guards on `code.startsWith(target)` and `desc.startsWith(target)` are
  // redundant and are kept only for symmetry: `target` is non-empty (early return
  // above) and `''.startsWith(nonEmpty)` is already false. Verified by mutation,
  // 2026-07-27 — removing either of those two fails NO test, removing the
  // `target.startsWith(code)` guard fails both null-phaseName tests below.
  // If you simplify here, that is the one to keep.
  return (
    code === target ||
    desc === target ||
    (code.length > 0 && code.startsWith(target)) ||
    (code.length > 0 && target.startsWith(code)) ||
    (desc.length > 0 && desc.startsWith(target))
  );
}

/**
 * Find the work-order phase on a job that matches Viewpoint udNBPhase.
 * VP pads phase codes (e.g. "SFTDEV.") while Sparta may show code or description.
 */
export function findNonBillableDefaultPhase<T extends ForemanPhaseLike>(
  workOrders: T[] | null | undefined,
  nbPhase: string | null | undefined
): T | null {
  const target = normalizeNbPhaseToken(nbPhase);
  if (!target) return null;

  const list = workOrders ?? [];
  return list.find((wo) => matchesNonBillablePhase(wo, target)) ?? null;
}

/**
 * True when VAC / SICK / HOL may be entered against THIS job and THIS work order.
 *
 * 🔴 THIS MIRRORS THE SERVER RULE ON PURPOSE, field for field:
 * `NonBillableEarnCodeRule.IsPlacementAllowed`. The clients previously enforced
 * only the JOB half, so leave was offered on any phase under the correct job while
 * submit rejected every phase but one. Change one side without the other and the
 * UI goes back to offering exactly what the API refuses.
 *
 * The two branches that look like holes are both deliberate and both match the
 * server:
 * - No job proven non-billable (including a missing job) → REFUSED. Fails closed
 *   mid navigation, same reasoning as {@link isNonBillableDefaultJob}.
 * - Job on file but NO udNBPhase → ALLOWED on any work order under that job. The
 *   phase switches itself on per employee as Viewpoint data lands; enforcing a
 *   phase we do not have would deny those employees leave entry entirely.
 *
 * Deliberately NOT earn-code aware: callers already know they are gating leave
 * fields, and taking an earn code here would invite passing 'st' and getting a
 * confident true that means nothing.
 *
 * 🔴 INVARIANT, AND IT IS THE REASON THIS FUNCTION READS `workOrders`: THE PHASE
 * HALF MAY NARROW FROM *ALL* WORK ORDERS TO *THE CORRECT ONE*, NEVER TO NONE.
 * If Viewpoint names a udNBPhase that matches NO work order on the job, phase
 * matching would refuse every work order and the employee could not enter leave
 * anywhere, which is a total lockout introduced by a gate rather than a fix. That
 * is the failure mode this app has already been burned by from the other side:
 * web forces the leave columns visible on the locked panel precisely because Tim
 * hit "the column is not there" while trying to book leave (2026-07-27).
 *
 * So an unreachable udNBPhase falls back to job-only enforcement, which is
 * exactly today's behaviour: no better, and provably no worse. Submit will still
 * reject, but the employee is not silently left with nowhere to put the hours,
 * and the data problem stays visible instead of being converted into a
 * disappeared field.
 */
export function isLeaveAllowedOnWorkOrder(
  job: NonBillableJobLike | null | undefined,
  workOrder: ForemanPhaseLike | null | undefined
): boolean {
  if (!isNonBillableDefaultJob(job)) return false;

  const nbPhase = normalizeNbPhaseToken(job?.nonBillableDefaultPhase);
  if (!nbPhase) return true;

  // Unreachable phase: enforce the job only. See the invariant above.
  if (!findNonBillableDefaultPhase(job?.workOrders, nbPhase)) return true;

  return matchesNonBillablePhase(workOrder, nbPhase);
}

/**
 * Ensure the NB default phase is always in the visible work-order set so the
 * employee can enter VAC/SICK/HOL without hunting for it under Work Orders ▾.
 */
export function ensureNonBillablePhaseVisible(
  desiredWorkOrderIds: string[],
  workOrders: ForemanPhaseLike[] | null | undefined,
  nbPhase: string | null | undefined
): string[] {
  const phase = findNonBillableDefaultPhase(workOrders, nbPhase);
  if (!phase?.foremanPhaseID) {
    return desiredWorkOrderIds;
  }
  if (desiredWorkOrderIds.includes(phase.foremanPhaseID)) {
    return desiredWorkOrderIds;
  }
  return [...desiredWorkOrderIds, phase.foremanPhaseID];
}
